export interface Role {
  id: string;
  name: string;
  reference?: string;
  description?: string;
  organizationId: string;
  scopes: RoleScope[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface UserRole {
  id: string;
  userId: string;
  roleId: string;
  scopes: RoleScope[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  isolated: boolean;
}

export interface RolePermission {
  id: string;
  roleId: string;
  permission: string;
  objectId?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

type RoleScopeQuestion = {
  type: 'question';
  ids: string[];
};

type RoleScopeDriver = {
  type: 'driver';
  ids: [];
};

type RoleScopeCategory = {
  type: 'category';
  ids: [];
};

type RoleScopeStructure = {
  type: 'structure';
  ids: [];
};

export type RoleScopeTimeInterval = {
  type: 'timeInterval';
  lowerBound?: Date;
  upperBound?: Date;
  maxAge?: string; // ISO 8601
};

type RoleScopeAttribute = {
  type: 'attribute';
  name: string;
  value: string[];
};

type RoleScopeLabel = {
  type: 'label';
  ids: string[];
};

type RoleScopeOwnFeedback = {
  type: 'ownFeedback';
  value: boolean;
};

type RoleScopeOwnUnits = {
  type: 'ownUnits';
  value: boolean;
};

type RoleScopeSelectedUnits = {
  type: 'selectedUnits';
  value: boolean;
};

export type RoleScope =
  | RoleScopeQuestion
  | RoleScopeDriver
  | RoleScopeCategory
  | RoleScopeTimeInterval
  | RoleScopeAttribute
  | RoleScopeStructure
  | RoleScopeLabel
  | RoleScopeOwnFeedback
  | RoleScopeOwnUnits
  | RoleScopeSelectedUnits;

// we need a list of all checkbox permission names because of calculating permission group checkmark:
// - checked, unchecked, indeterminate
export const componentsAccessPermissionNames = [
  'campaign.read',
  'campaign.update',
  'campaign.full',
  'dashboard.read',
  'analyticsDashboard.full',
  'feedback.read',
  'feedback.readHistory',
  'feedback.repliesRead',
  'feedback.reply',
  'feedback.share',
  'feedback.discussionRead',
  'feedback.discussionStart',
  'feedback.update.annotate',
  'feedback.update.star',
  'feedback.update.move',
  'feedback.update.responded',
  'feedback.update.related',
  'feedback.update.visibility',
  'feedback.update.values',
  'feedback.label.read',
  'feedback.driver.read',
  'flow.read',
  'flow.draft',
  'flow.update',
  'flow.full',
  'distributionDashboard.read',
  'dataTrigger.manualImport',
  'dataTrigger.read',
  'dataTrigger.update',
  'dataTrigger.full',
  'generator.linksList',
  'generator.read',
  'generator.update',
  'generator.full',
  'embedWidget.read',
  'embedWidget.update',
  'embedWidget.full',
  'filter',
  'selectTime',
  'export.widget',
  'export.data',
  'personalAccount.update.name',
  'personalAccount.update.image',
  'personalAccount.update.tz',
  'label.assign',
  'driver.assign',
];

export const administrationPermissionNames = [
  'account.read',
  'account.update',
  'account.create',
  'account.delete',
  'profile.read',
  'profile.update',
  'profile.create',
  'profile.delete',
  'category.read',
  'category.update',
  'category.full',
  'structure.read',
  'structure.update',
  'workspace.read',
  'workspace.update',
  'workspace.full',
  'attributes.read',
  'attributes.update',
  'attributes.create',
  'attributes.delete',
  'dataSource.read',
  'dataSource.update',
  'dataSource.create',
  'dataSource.delete',
  'notification.read',
  'notification.update',
  'notification.full',
  'autoreplyTemplate.read',
  'autoreplyTemplate.update',
  'autoreplyTemplate.full',
  'autoreplyCondition.read',
  'autoreplyCondition.update',
  'autoreplyCondition.full',
  'template.read',
  'template.update',
  'template.full',
  'label.read',
  'label.update',
  'label.full',
  // 'label.applyOnFeedback',
  'driver.read',
  'driver.update',
  'driver.create',
  'driver.delete',
  // 'driver.applyOnFeedback',
  'predefinedAnswer.read',
  'predefinedAnswer.update',
  'predefinedAnswer.full',
  'channel.read',
  'channel.update',
  'channel.full',
  'throttling.read',
  'throttling.update',
  'throttling.full',
  'role.read',
  'role.update',
  'role.full',
  'auditLog.read',
  'terminal.read',
  'terminal.update',
  'terminal.full',
];

export const fbDetailsVisibilityPermissionNames = [
  'feedback.read.createdAt',
  'feedback.read.replied',
  'feedback.read.flow',
  'feedback.read.interactionReason',
  'feedback.read.interactionSource',
  'feedback.read.source',
  'customer.read.id',
  'customer.read.name',
  'customer.read.email',
  'customer.read.phone',
  'customer.read.ipAddress',
  'customer.read.interactionId',
  'customer.read.locale',
  'customer.read.createdAt',
  'employee.read.name',
  'employee.read.reference',
  'employee.read.email',
  'visibility.questions',
  'visibility.attributes',
];

export const roleScopeNames = [
  'scope.ownFeedback',
  'scope.ownUnits',
  'scope.selectedUnits',
  'scope.timeInterval',
  'scope.attributes',
  'scope.questions',
  'scope.categories',
  'scope.structure',
  'scope.labels',
  'scope.drivers',
];

export const checkboxPermissionNames = [
  ...componentsAccessPermissionNames,
  ...administrationPermissionNames,
  ...fbDetailsVisibilityPermissionNames,
];
